/**
 * Product-only catalog controls for the F&S global search page.
 * This is intentionally scoped to search results: Journal rows never enter the
 * filter, sort, count, or empty-state path.
 */
(() => {
  const optionLabels = {
    color: {
      transparent: 'Clear', white: 'White', green: 'Green', blue: 'Blue', pink: 'Pink', purple: 'Purple',
      red: 'Red', orange: 'Orange', yellow: 'Yellow', gold: 'Gold', brown: 'Brown', grey: 'Grey',
      black: 'Black', multicolor: 'Multicolor',
    },
  };

  const colorSwatches = {
    transparent: 'swatch-transparent', white: 'swatch-white', green: 'swatch-green', blue: 'swatch-blue',
    pink: 'swatch-pink', purple: 'swatch-purple', red: 'swatch-red', orange: 'swatch-orange',
    yellow: 'swatch-yellow', gold: 'swatch-gold', brown: 'swatch-brown', grey: 'swatch-grey',
    black: 'swatch-black', multicolor: 'swatch-multicolor',
  };

  const priceRanges = {
    '50-99': (price) => price >= 50 && price <= 99,
    '100-149': (price) => price >= 100 && price <= 149,
    '150-199': (price) => price >= 150 && price <= 199,
    '200-299': (price) => price >= 200 && price <= 299,
    '300-499': (price) => price >= 300 && price <= 499,
    '500-799': (price) => price >= 500 && price <= 799,
    '800-plus': (price) => price >= 800,
  };

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function tokenize(value) {
    return String(value || '')
      .split(/[;,|]/)
      .map(normalize)
      .filter(Boolean);
  }

  function mount(root) {
    if (root.dataset.fsSearchMounted === 'true') return;
    const productSection = root.querySelector('[data-fs-search-products]');
    if (!productSection) return;

    const products = [...productSection.querySelectorAll('.shop-product-card')];
    const productGrid = productSection.querySelector('[data-fs-search-product-grid]');
    const filterGroups = productSection.querySelector('[data-fs-search-filter-groups]');
    const filterToggle = productSection.querySelector('[data-fs-search-filter-toggle]');
    const filterClose = productSection.querySelector('[data-fs-search-filter-close]');
    const filterBackdrop = productSection.querySelector('[data-fs-search-filter-backdrop]');
    const viewProducts = productSection.querySelector('[data-fs-search-view-products]');
    const sort = productSection.querySelector('[data-fs-search-sort]');
    const count = productSection.querySelector('[data-fs-search-product-count]');
    const drawerCount = productSection.querySelector('[data-fs-search-drawer-count]');
    const mobileFilterCount = productSection.querySelector('[data-fs-search-mobile-filter-count]');
    const activeFilters = productSection.querySelector('[data-fs-search-active-filters]');
    const filterChips = productSection.querySelector('[data-fs-search-filter-chips]');
    const clearFilters = productSection.querySelector('[data-fs-search-clear-filters]');
    const clearEmpty = productSection.querySelector('[data-fs-search-clear-empty]');
    const emptyState = productSection.querySelector('[data-fs-search-empty-products]');
    const filterMenus = [...productSection.querySelectorAll('.shop-filter-menu')];
    const mobileQuery = window.matchMedia('(max-width: 700px)');
    let lastDrawerTrigger = null;

    function makeOption(name, value) {
      const label = optionLabels[name]?.[value]
        || value.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
      const swatch = name === 'color'
        ? `<span class="color-swatch ${colorSwatches[value] || 'swatch-multicolor'}" aria-hidden="true"></span>`
        : '';
      return `<label class="shop-filter-option"><input type="checkbox" name="${name}" value="${value}">${swatch}<span>${label}</span></label>`;
    }

    productSection.querySelectorAll('[data-fs-search-dynamic-options]').forEach((panel) => {
      const name = panel.dataset.fsSearchDynamicOptions;
      const values = [...new Set(products.flatMap((product) => tokenize(product.dataset[name])))].sort((a, b) => {
        return (optionLabels[name]?.[a] || a).localeCompare(optionLabels[name]?.[b] || b);
      });
      panel.innerHTML = values.map((value) => makeOption(name, value)).join('');
    });

    const filterInputs = [...productSection.querySelectorAll('.shop-filter-option input')];

    function selectedValues(name) {
      return filterInputs.filter((input) => input.name === name && input.checked).map((input) => input.value);
    }

    function matchesValues(product, name, selected) {
      if (!selected.length) return true;
      const values = tokenize(product.dataset[name]);
      return selected.some((value) => values.includes(value));
    }

    function matchesPrice(product, selected) {
      if (!selected.length) return true;
      if (product.dataset.price === 'request') return false;
      const price = Number(product.dataset.price);
      return selected.some((range) => priceRanges[range](price));
    }

    function productMatches(product) {
      return matchesValues(product, 'type', selectedValues('type'))
        && matchesValues(product, 'color', selectedValues('color'))
        && matchesValues(product, 'state', selectedValues('state'))
        && matchesValues(product, 'material', selectedValues('material'))
        && matchesPrice(product, selectedValues('price'));
    }

    function inputLabel(input) {
      return input.closest('label')?.querySelector('span:last-child')?.textContent?.trim() || input.value;
    }

    function renderActiveFilters() {
      const selected = filterInputs.filter((input) => input.checked);
      filterChips.replaceChildren();
      selected.forEach((input) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'shop-filter-chip';
        chip.textContent = `${inputLabel(input)} ×`;
        chip.setAttribute('aria-label', `Remove ${inputLabel(input)} filter`);
        chip.addEventListener('click', () => {
          input.checked = false;
          updateProducts();
        });
        filterChips.append(chip);
      });
      activeFilters.hidden = selected.length === 0;
      mobileFilterCount.textContent = selected.length;
      filterMenus.forEach((menu) => {
        const menuSelected = menu.querySelectorAll('input:checked').length;
        const status = menu.querySelector('.filter-selected-count');
        if (status) status.textContent = menuSelected ? `(${menuSelected})` : '';
      });
    }

    function sortProducts() {
      const sortValue = sort?.value || 'relevance';
      const sorted = [...products].sort((a, b) => {
        if (sortValue === 'newest') return Number(b.dataset.newness) - Number(a.dataset.newness);
        if (sortValue.startsWith('price')) {
          const aRequest = a.dataset.price === 'request';
          const bRequest = b.dataset.price === 'request';
          if (aRequest !== bRequest) return aRequest ? 1 : -1;
          const aPrice = Number(a.dataset.price);
          const bPrice = Number(b.dataset.price);
          return sortValue === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;
        }
        return Number(a.dataset.order) - Number(b.dataset.order);
      });
      sorted.forEach((product) => productGrid.append(product));
    }

    function updateProducts() {
      const visibleProducts = products.filter(productMatches);
      products.forEach((product) => {
        product.hidden = !visibleProducts.includes(product);
      });
      count.textContent = visibleProducts.length;
      drawerCount.textContent = visibleProducts.length;
      emptyState.hidden = visibleProducts.length !== 0;
      productGrid.hidden = visibleProducts.length === 0;
      renderActiveFilters();
      sortProducts();
    }

    function setDrawer(open) {
      if (!filterGroups || !mobileQuery.matches) return;
      filterGroups.classList.toggle('is-open', open);
      filterGroups.setAttribute('aria-hidden', String(!open));
      filterGroups.inert = !open;
      filterToggle?.setAttribute('aria-expanded', String(open));
      if (filterBackdrop) filterBackdrop.hidden = !open;
      document.body.classList.toggle('filter-drawer-open', open);

      if (open) {
        lastDrawerTrigger = document.activeElement;
        window.requestAnimationFrame(() => filterClose?.focus());
      } else if (lastDrawerTrigger instanceof HTMLElement) {
        const returnTarget = lastDrawerTrigger;
        lastDrawerTrigger = null;
        window.requestAnimationFrame(() => returnTarget.focus());
      }
    }

    function syncDrawerAccessibility() {
      if (!filterGroups) return;
      if (mobileQuery.matches) {
        filterGroups.setAttribute('aria-hidden', 'true');
        filterGroups.inert = true;
      } else {
        filterGroups.classList.remove('is-open');
        filterGroups.setAttribute('aria-hidden', 'false');
        filterGroups.inert = false;
        filterToggle?.setAttribute('aria-expanded', 'false');
        if (filterBackdrop) filterBackdrop.hidden = true;
        document.body.classList.remove('filter-drawer-open');
      }
    }

    filterInputs.forEach((input) => input.addEventListener('change', updateProducts));
    sort?.addEventListener('change', sortProducts);
    filterToggle?.addEventListener('click', () => setDrawer(true));
    filterClose?.addEventListener('click', () => setDrawer(false));
    filterBackdrop?.addEventListener('click', () => setDrawer(false));
    viewProducts?.addEventListener('click', () => setDrawer(false));
    clearFilters?.addEventListener('click', () => {
      filterInputs.forEach((input) => { input.checked = false; });
      updateProducts();
    });
    clearEmpty?.addEventListener('click', () => {
      filterInputs.forEach((input) => { input.checked = false; });
      updateProducts();
    });
    mobileQuery.addEventListener?.('change', syncDrawerAccessibility);

    syncDrawerAccessibility();
    updateProducts();
    root.dataset.fsSearchMounted = 'true';
  }

  function mountAll(root = document) {
    root.querySelectorAll('[data-fs-search-results]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  } else {
    mountAll();
  }
})();
